import {
  type IncidentDetail,
  MONITOR_SIMULATION_NOTE,
  monitorKindOf,
  monitorTargetLabel,
} from '@fos/shared/contracts/monitoring';
import {
  checksCountText,
  failThresholdText,
  INCIDENT_STATUS_LABELS,
  INCIDENT_STATUS_TONES,
  incidentDurationText,
  MONITOR_MODE_LABELS,
  monitorIntervalLabel,
  recoverThresholdText,
} from '@fos/shared/labels';
import { ExternalLink, FlaskConical, Mail, Siren } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { formatDateShort, formatRelativePast, formatTime } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Breadcrumbs } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Dot } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { DelegateToClaudeButton } from '../../agents/components/DelegateToClaudeButton';
import type { DelegateTarget } from '../../agents/components/DelegateToClaudeDialog';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { ModulePage, NotFoundPage } from '../../modules/pages/ModulePage';
import { segmentRangeText } from '../monitoring-format';

/**
 * Incydent monitoringu `/incydenty/:id` (czeka na projekt w Figmie — wzorzec karty zasobu): tytuł i stan, od kiedy
 * i jak długo, przyczyna, oś czasu sprawdzeń w odcinkach (np. „9:40–9:42 · 2 sprawdzenia · przekroczono czas”),
 * powiązane wiadomości klienta (moduł Inbox), monitor i zasób. Symulacja wyraźnie oznaczona.
 */
export function IncidentPage() {
  const access = useAccess();
  const { id } = useParams();
  if (!access.can('maintenance')) return <ModulePage module="maintenance" />;
  if (!id) return <NotFoundPage />;
  return <IncidentView id={id} />;
}

function IncidentView({ id }: { id: string }) {
  const query = trpc.monitoring.getIncident.useQuery({ id }, { refetchInterval: 30_000 });
  if (query.isPending) {
    return (
      <Page>
        <div role="status" aria-label="Wczytywanie incydentu">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-4 h-7 w-72" />
          <Skeleton className="mt-2 h-4 w-96 max-w-full" />
          <div className="mt-8 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </div>
      </Page>
    );
  }
  if (query.isError) {
    return (
      <Page>
        <Breadcrumbs items={[{ label: 'Utrzymanie', to: '/utrzymanie' }]} />
        <Section className="mt-6">
          {appErrorCode(query.error) === 'NOT_FOUND' ? (
            <EmptyState
              icon={Siren}
              title="Nie ma takiego incydentu"
              description="Incydent mógł dotyczyć zasobu, do którego nie masz dostępu."
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <IncidentContent detail={query.data} />;
}

function dateTime(iso: string): string {
  return `${formatDateShort(iso)}, ${formatTime(iso)}`;
}

/** Sekcja strony incydentu = biały blok (ui/Section, DECISIONS 2026-10-07 „Nowy układ wizualny”). */
function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Section aria-label={title}>
      <h2 className="text-body-s font-semibold text-fg">{title}</h2>
      <div className="mt-2">{children}</div>
    </Section>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <dt className="shrink-0 text-caption text-fg-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-caption text-fg">{children}</dd>
    </div>
  );
}

/**
 * „Zleć Claude” przy incydencie (Fala 9): instrukcja z faktów monitoringu (bez treści maili klienta — to dane osób
 * trzecich), zlecenie przy kliencie incydentu (bez projektu: wykonawca domyślnie sesja Claude przez MCP; lokalny runner
 * po wskazaniu repozytorium w oknie).
 */
function incidentDelegation(detail: IncidentDetail): DelegateTarget {
  const { incident, monitor, asset, client } = detail;
  const when = incident.resolvedAt
    ? `od ${dateTime(incident.openedAt)} do ${dateTime(incident.resolvedAt)} (zamknięty)`
    : `od ${dateTime(incident.openedAt)} (trwa)`;
  const lines = [
    `Awaria z monitoringu Fravity OS: ${incident.title}${incident.simulated ? ' (symulacja)' : ''}.`,
    // Fala 10: port serwera = host:port, meldunek = „meldunek serwera” (adres heartbeat:// to tylko id monitora)
    `Monitor: ${monitor.name} — ${monitorTargetLabel(monitor.url)}; sprawdzanie ${monitorIntervalLabel(monitor.intervalSec)}.`,
    asset ? `Zasób: ${asset.name}.` : null,
    `Czas: ${when}; nieudane sprawdzenia: ${detail.checks.failed} z ${detail.checks.total}.`,
    `Przyczyna wg monitoringu: ${incident.cause ?? 'brak'}.`,
    '',
    'Ustal przyczynę (logi serwera i aplikacji, konfiguracja, ostatnie zmiany), napraw ją i opisz, co zrobiono i jak to sprawdzono.',
    'Wdrożenie na produkcję tylko przez akceptację w Fravity OS. Nie odczytuj ani nie zmieniaj haseł i kluczy.',
  ];
  return {
    ...(incident.clientId ? { clientId: incident.clientId } : {}),
    contextLabel: [`Incydent: ${monitor.name}`, client?.name].filter(Boolean).join(' · '),
    title: `Awaria: ${monitor.name}`.slice(0, 200),
    instruction: lines.filter((l) => l !== null).join('\n'),
  };
}

function IncidentContent({ detail }: { detail: IncidentDetail }) {
  const access = useAccess();
  const { incident, monitor, asset, client } = detail;
  const kind = monitorKindOf(monitor.url);
  const open = incident.status === 'open';
  const crumbs = [
    { label: 'Utrzymanie', to: '/utrzymanie' },
    ...(asset ? [{ label: asset.name, to: `/utrzymanie/${asset.id}` }] : []),
  ];
  const duration = incidentDurationText(incident.durationSec);
  const meta = [
    open
      ? `od ${dateTime(incident.openedAt)} · trwa ${duration}`
      : `${dateTime(incident.openedAt)}–${formatTime(incident.resolvedAt as string)} · trwał ${duration}`,
    client ? `klient ${client.name}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Page>
      <Breadcrumbs items={crumbs} />
      <header className="mt-2.5 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="min-w-0 text-h3 text-fg">{incident.title}</h1>
            <StatusPill tone={INCIDENT_STATUS_TONES[incident.status]}>
              {INCIDENT_STATUS_LABELS[incident.status]}
            </StatusPill>
          </div>
          <p className="text-body-s text-fg-secondary">{meta}</p>
        </div>
        {(asset || access.can('agents', 'edit')) && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* Fala 9: awaria = sprawa operacyjna dla Claude (DECISIONS 2026-10-07), zlecenie przy kliencie incydentu */}
            <DelegateToClaudeButton target={() => incidentDelegation(detail)} />
            {asset && (
              <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                <Link to={`/utrzymanie/${asset.id}`}>Przejdź do zasobu</Link>
              </Button>
            )}
          </div>
        )}
      </header>

      {incident.simulated && (
        <ContextBanner tone="info" icon={FlaskConical} className="mt-[18px]">
          {MONITOR_SIMULATION_NOTE}. Incydent powstał z przełącznika „Symuluj awarię”.
        </ContextBanner>
      )}

      <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4 md:gap-6">
          <Block title="Przebieg">
            {incident.cause && (
              <p className="mb-3 text-body-s text-fg-secondary">
                Przyczyna: <span className="text-fg">{incident.cause}</span>
              </p>
            )}
            {detail.timeline.length === 0 ? (
              <p className="text-body-s text-fg-muted">Brak zapisanych sprawdzeń (starsze niż 90 dni są usuwane).</p>
            ) : (
              <ol className="flex flex-col">
                {detail.timeline.map((s) => (
                  <li key={`${s.from}-${s.ok}`} className="flex items-start gap-3 border-b border-line py-2.5">
                    <Dot tone={s.ok ? 'success' : 'danger'} className="mt-[7px]" />
                    <div className="min-w-0 flex-1">
                      <p className="text-body-s text-fg">
                        {s.ok ? 'Działa' : (s.error ?? 'Błąd')}
                        {s.ok && s.simulated && <span className="text-fg-muted"> · symulacja</span>}
                      </p>
                      <p className="text-caption text-fg-muted">
                        {segmentRangeText(s.from, s.to)} · {checksCountText(s.count)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {open && (
              <p className="mt-3 text-caption text-fg-muted">
                Incydent zamknie się sam {recoverThresholdText(monitor.recoverThreshold)}.
              </p>
            )}
          </Block>

          <Block title="Wiadomości klienta">
            {detail.messagesHidden ? (
              <p className="text-body-s text-fg-muted">Wiadomości widzą osoby z dostępem do Inboxu.</p>
            ) : detail.messages.length === 0 ? (
              <p className="text-body-s text-fg-muted">Brak wiadomości od klienta w sprawie tej awarii.</p>
            ) : (
              <ul className="flex flex-col">
                {detail.messages.map((m) => (
                  <li key={m.id} className="border-b border-line">
                    <Link
                      to={`/poczta/${m.threadId}`}
                      className="-mx-2 flex items-start gap-3 rounded-lg px-2 py-2.5 hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <Mail size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body-s text-fg">{m.subject || '(bez tematu)'}</p>
                        <p className="truncate text-caption text-fg-muted">
                          {m.fromName ?? m.fromEmail} · {formatRelativePast(m.sentAt)}
                        </p>
                        {m.snippet && <p className="mt-0.5 line-clamp-2 text-caption text-fg-secondary">{m.snippet}</p>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>

        <aside className="flex flex-col gap-4">
          <Section aria-label="Monitor" className="md:px-6">
            <h2 className="text-caption text-fg-muted">Monitor</h2>
            <dl className="mt-2">
              <InfoRow label={kind === 'http' ? 'Adres' : kind === 'tcp' ? 'Port' : 'Rodzaj'}>
                {kind === 'http' ? (
                  <a
                    href={monitor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 rounded-xs hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
                  >
                    {monitor.name}
                    <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
                  </a>
                ) : kind === 'tcp' ? (
                  <Mono>{monitorTargetLabel(monitor.url)}</Mono>
                ) : (
                  'Meldunek serwera'
                )}
              </InfoRow>
              <InfoRow label="Sprawdzanie">{monitorIntervalLabel(monitor.intervalSec)}</InfoRow>
              <InfoRow label="Alarm">{failThresholdText(monitor.failThreshold)}</InfoRow>
              <InfoRow label="Tryb">{MONITOR_MODE_LABELS[monitor.mode]}</InfoRow>
              <InfoRow label="Sprawdzenia">
                {detail.checks.failed} z {detail.checks.total} nieudane
              </InfoRow>
            </dl>
          </Section>
        </aside>
      </div>
    </Page>
  );
}
